import { Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useI18n, type Lang } from "@/lib/i18n";
import { storiesListQuery, categoriesAllQuery, formatLogDate, type StoryListItem } from "@/lib/stories";
import { CATEGORIES } from "@/lib/categories";

export function StoryFeed({ lang }: { lang: Lang }) {
  const { t } = useI18n();
  const { data: stories } = useSuspenseQuery(storiesListQuery(lang));
  const { data: cats } = useSuspenseQuery(categoriesAllQuery());
  const idToSlug = new Map(cats.map((c) => [c.id, c.slug]));

  const latest = (stories ?? []).slice(0, 3);
  if (latest.length === 0) return null;

  return (
    <section id="stories" className="mx-auto max-w-5xl px-5 pb-16 md:pb-20">
      <div className="mb-8 flex items-baseline justify-between">
        <h2 className="font-display text-2xl sm:text-3xl">{t.feed_latest}</h2>
        <Link
          to="/$lang/stories"
          params={{ lang }}
          className="text-sm text-muted-foreground underline decoration-dotted underline-offset-4 hover:text-foreground"
        >
          {t.feed_view_all}
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {latest.map((story) => (
          <StoryCard key={story.id} story={story} lang={lang} idToSlug={idToSlug} />
        ))}
      </div>
    </section>
  );
}

function StoryCard({
  story,
  lang,
  idToSlug,
}: {
  story: StoryListItem;
  lang: Lang;
  idToSlug: Map<string, string>;
}) {
  const { t } = useI18n();
  const catSlug = story.category_ids?.[0] ? idToSlug.get(story.category_ids[0]) : undefined;
  const category = catSlug ? CATEGORIES.find((c) => c.slug === catSlug) : undefined;

  const title = story.title ?? "Untitled entry";
  const displayTitle = title.length > 90 ? title.slice(0, 90) + "\u2026" : title;

  return (
    <a
      href={`/${lang}/stories/${story.slug}`}
      className="group flex flex-col rounded-sm border border-border bg-card/40 p-5 transition-colors hover:bg-card/70"
    >
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        <span>{formatLogDate(story.published_at, lang)}</span>
        <span className="text-border">·</span>
        <span>{t.feed_anonymous}</span>
      </div>
      <h3 className="font-display text-lg leading-snug group-hover:underline decoration-dotted underline-offset-4">
        {displayTitle}
      </h3>
      {category && (
        <div className="mt-auto pt-4">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-[10px] uppercase tracking-[0.15em]">
            <span className="font-display font-semibold">{category.flag.toUpperCase()}</span>
            <span>{lang === "ru" ? category.label.ru : category.label.en}</span>
          </span>
        </div>
      )}
      <div className="mt-3 text-sm text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
        {t.feed_read_more}
      </div>
    </a>
  );
}